import Modules from 'services/crud/Modules';
import Settings from 'services/crud/settings';
import Layer from 'services/models/Layer';

/**
 * useContext usado para forcener uma maneira de passar dados
 * pela arvore de componentes sem ter que passar props manualmente
 * em todos os níveis.
 * 
 * useState é um hook que nos permite rastrear o estado em um
 * componente de função, o estado geralmente se refere a dados ou
 * propriedads que precisam ser rastreador em um aplicativo.
 * 
 * useEffect é um hook que permite executar efeitos colaterais em
 * seus componentes, por exemplo: buscar dados, atualizar diretamente
 * o DOM e cronômetros, ele aceita dois argumentos o segundo é opcional.
 * 
 * useCallback é um hook que retorna uma função de retorno
 * de chamada memorizada
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useParams } from 'react-router-dom';

export const Context = createContext({});

export const Provider = ({ children }) => {
  const [modules, setModules] = useState([]);
  const [activeModule, setActiveModule] = useState(null);

  const params = useParams();

  /**
   * Redefine o módulo da forma inicial
   */
  const resetModule = useCallback(() => {
    setActiveModule(null);
    let currentModule = { ...activeModule };
    currentModule.camadas &&
      currentModule.camadas.camadas &&
      currentModule.camadas.camadas.forEach((c) => c.setSelected(false));
    setActiveModule(currentModule);
  }, [activeModule]);

  /**
   * Define um módulo como ativo de acordo com a URL recebida
   */
  const setActiveModuleByUrl = useCallback(
    (url, modulesList) => {
      modulesList = modulesList || modules;
      let module = modulesList.find((m) => m.endereco === url);
      if (module) {
        let layersClass = module?.camadas?.camadas?.map((c) => {
          let lc = new Layer(
            c.categoria_mae,
            c.tipo,
            c.servico_externo_id,
            c.apelido,
            c.resumo,
            c.nome_geoserver,
            c.workspace_geoserver,
            c.configuracao,
            c.ordem
          );
          lc.type === 'externa' && lc.loadData();
          return lc;
        });
        setActiveModule({
          ...module,
          camadas: { ...module.camadas, camadas: layersClass },
        });
      }
    },
    [modules]
  );

  /**
   * Retorna um elemento e suas configurações, ou null
   * @param {*} elementId
   * @returns
   */
  const getElement = useCallback(
    (elementId) => {
      let element;
      if (activeModule?.elementosConfig) {
        element = activeModule.elementosConfig[elementId];
      }
      return element ? element : null;
    },
    [activeModule]
  );

  /**
   * Defeine configurações em um elemento
   * 
   * @param {number} elementId
   * @param {string} atribute
   * @param {object} value
   * @returns
   */
  const setElement = useCallback(
    (elementId, atribute, value) => {
      let element;
      if (activeModule?.elementosConfig) {
        element = activeModule.elementosConfig[elementId];
        if (element) {
          element.configuracao[atribute] = value;
          setActiveModule(activeModule);
        }
      }
      return element ? element : null;
    },
    [activeModule]
  );

  /**
   * Carrega todos os módulos
   */
  useEffect(() => {
    if (!modules.length) {
      Modules.get().then((response) => {
        if (response) {
          let modulesList = response.sort((a, b) =>
            a.titulo > b.titulo ? 1 : a.titulo < b.titulo ? -1 : 0
          );
          // Carrega o módulo principal
          Settings.getByKey('modulo_principal').then((paramResponse) => {
            let moduleId = paramResponse._embedded.configuracao[0].valor;
            let mainModule = modulesList.find(
              (m) =>
                m.tipo_modulo === 'interno' &&
                m.id.toString() === moduleId.toString()
            );
            if (mainModule) mainModule.isMain = true;
            setModules(modulesList);
          });
        }
      });
    }
  }, [modules]);

  /**
   * Define o módulo ativo, pela URL
   */
  useEffect(() => {
    if (params.module) {
      setActiveModule(null);
      setActiveModuleByUrl(params.module);
    } else {
      let mainModule = modules.find((m) => m.isMain);
      setActiveModuleByUrl(mainModule?.endereco || params.module);
    }
  }, [modules, params.module, setActiveModuleByUrl]);

  return (
    <Context.Provider
      value={{
        modules,
        activeModule,
        resetModule,
        getElement,
        setElement,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export function useModule() {
  const context = useContext(Context);

  if (!context) {
    throw new Error('useMap must be used within an MapProvider');
  }
  return context;
}

/**
 * withModule
 * 
 * @param {*} WrappedComponent 
 * 
 * @returns 
 */
export function withModule(WrappedComponent) {
  return (props) => {
    const context = useContext(Context);

    if (!context) {
      throw new Error('withMap must be used within an MapProvider');
    }
    return <WrappedComponent {...props} moduleContext={context} />;
  };
}
